import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { PublicPage, PublicCard } from '../PublicPage';
import './contactUs.scss';

/**
 * Contact Us, matching demo.facilonservices.com/contact-us.html — the same details,
 * the same "How can we help you?" enquiry form and the same field set, rebuilt in our
 * theme (user decision 2026-09-12).
 *
 * The demo posts to a PHP mailer we have no equivalent of. Until a public enquiry
 * endpoint exists, submitting opens a pre-filled draft in the visitor's mail app — and
 * because that silently does nothing for anyone without one, the confirmation panel
 * always shows the address and offers the message on the clipboard as a fallback.
 */

const PHONE = '+91 8976917026';
const PHONE_HREF = 'tel:+918976917026';

/**
 * PLACEHOLDER — this is a test environment, so enquiries must not reach the real
 * inbox (facilon.services@facilonservices.com). example.com is reserved by RFC 2606
 * and can never deliver, so a stray test submission cannot escape.
 *
 * Before production: set REACT_APP_CONTACT_EMAIL to the real address.
 */
const EMAIL = process.env.REACT_APP_CONTACT_EMAIL || 'facilon.test@example.com';
const IS_PLACEHOLDER_EMAIL = EMAIL.endsWith('@example.com');

const ADDRESS =
  'Facilon Services Private Ltd. A102, Golden Square CHS, Kalina, Santacruz (E), Vidya Nagari, Mumbai - 400098, India.';

interface FormState {
  firstName: string;
  lastName: string;
  email: string;
  phone: string;
  country: string;
  jobTitle: string;
  company: string;
  comments: string;
  consent: boolean;
}

const EMPTY: FormState = {
  firstName: '',
  lastName: '',
  email: '',
  phone: '',
  country: '',
  jobTitle: '',
  company: '',
  comments: '',
  consent: false,
};

const REQUIRED: { key: keyof FormState; label: string }[] = [
  { key: 'firstName', label: 'First Name' },
  { key: 'lastName', label: 'Last Name' },
  { key: 'email', label: 'Email Address' },
  { key: 'phone', label: 'Phone Number' },
  { key: 'country', label: 'Country' },
];

export const ContactUs: React.FC = () => {
  const [form, setForm] = useState<FormState>(EMPTY);
  const [errors, setErrors] = useState<Partial<Record<keyof FormState, string>>>({});
  /** The composed message, kept after submit so the fallback can offer it. */
  const [sent, setSent] = useState<string | null>(null);
  const [copied, setCopied] = useState(false);

  const set = (key: keyof FormState, value: string | boolean) => {
    setForm((f) => ({ ...f, [key]: value }));
    setErrors((e) => ({ ...e, [key]: undefined }));
  };

  const validate = () => {
    const next: Partial<Record<keyof FormState, string>> = {};
    REQUIRED.forEach(({ key, label }) => {
      if (!String(form[key]).trim()) next[key] = `${label} is required`;
    });
    if (form.email.trim() && !/^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/.test(form.email.trim())) {
      next.email = 'Enter a valid email address';
    }
    if (!form.consent) next.consent = 'Please accept the Terms of use and Privacy Policy';
    return next;
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    const next = validate();
    setErrors(next);
    if (Object.keys(next).length) return;

    // filter the optional fields out first — a later filter(Boolean) would also strip
    // the blank line that separates the details from the visitor's own message
    const details = [
      `Name: ${form.firstName} ${form.lastName}`,
      `Email: ${form.email}`,
      `Phone: ${form.phone}`,
      `Country: ${form.country}`,
      form.jobTitle && `Job Title: ${form.jobTitle}`,
      form.company && `Company: ${form.company}`,
    ].filter(Boolean);

    const body = `${details.join('\n')}\n\n${form.comments.trim() || '(no comments)'}`;

    const subject = `Website enquiry from ${form.firstName} ${form.lastName}`;

    // Show the confirmation first: if the visitor has no mail client this navigation
    // does nothing at all, and the panel is then the only thing telling them what to do.
    setSent(`${subject}\n\n${body}`);
    setCopied(false);
    window.location.href =
      `mailto:${EMAIL}?subject=${encodeURIComponent(subject)}&body=${encodeURIComponent(body)}`;
  };

  const copyMessage = async () => {
    if (!sent) return;
    try {
      await navigator.clipboard.writeText(sent);
      setCopied(true);
    } catch {
      setCopied(false);
    }
  };

  const startOver = () => {
    setForm(EMPTY);
    setErrors({});
    setSent(null);
    setCopied(false);
  };

  const field = (
    key: keyof FormState,
    placeholder: string,
    type: 'text' | 'email' | 'tel' = 'text',
  ) => (
    <div className="contact-field">
      <label htmlFor={key} className="sr-only-label">
        {placeholder}
      </label>
      <input
        id={key}
        name={key}
        type={type}
        className={`contact-input ${errors[key] ? 'has-error' : ''}`}
        placeholder={placeholder}
        value={String(form[key])}
        onChange={(e) => set(key, e.target.value)}
        aria-invalid={!!errors[key]}
        aria-describedby={errors[key] ? `${key}-error` : undefined}
      />
      {errors[key] && (
        <p className="contact-error" id={`${key}-error`}>
          {errors[key]}
        </p>
      )}
    </div>
  );

  return (
    <PublicPage title="Contact Us" subtitle="How to reach Facilon." icon="bi-envelope-fill">
      <div className="contact-detail-grid">
        <PublicCard>
          <div className="contact-row">
            <i className="bi bi-telephone-fill"></i>
            <div>
              <p className="contact-label">Get in touch</p>
              <a href={PHONE_HREF}>{PHONE}</a>
            </div>
          </div>
        </PublicCard>
        <PublicCard>
          <div className="contact-row">
            <i className="bi bi-envelope-fill"></i>
            <div>
              <p className="contact-label">E-mail</p>
              <a href={`mailto:${EMAIL}`}>{EMAIL}</a>
            </div>
          </div>
        </PublicCard>
        <PublicCard>
          <div className="contact-row">
            <i className="bi bi-geo-alt-fill"></i>
            <div>
              <p className="contact-label">Location</p>
              <address className="contact-address">{ADDRESS}</address>
            </div>
          </div>
        </PublicCard>
      </div>

      {IS_PLACEHOLDER_EMAIL && (
        <div className="contact-testing-note">
          <i className="bi bi-cone-striped"></i>
          <span>
            Test environment — enquiries are addressed to a reserved placeholder and cannot
            reach a real inbox. Set <code>REACT_APP_CONTACT_EMAIL</code> before going live.
          </span>
        </div>
      )}

      {sent ? (
        <PublicCard title="Your message is ready to send" icon="bi-check-circle-fill">
          <div className="contact-sent">
            <p>
              We have opened a pre-filled draft in your email app addressed to{' '}
              <a href={`mailto:${EMAIL}`}>{EMAIL}</a>. <strong>It has not been sent yet</strong> —
              press send there to deliver it.
            </p>
            <p className="contact-sent-fallback">
              Nothing opened? Your browser may have no mail app set up. Copy the message and
              send it from wherever you read email.
            </p>
            <pre className="contact-sent-preview">{sent}</pre>
            <div className="contact-sent-actions">
              <button type="button" className="contact-submit" onClick={copyMessage}>
                {copied ? 'Copied' : 'Copy message'}
              </button>
              <button type="button" className="contact-secondary" onClick={startOver}>
                Write another message
              </button>
            </div>
          </div>
        </PublicCard>
      ) : (
      <PublicCard title="How can we help you?" icon="bi-chat-left-text-fill">
        <form className="contact-form" onSubmit={handleSubmit} noValidate>
          <div className="contact-grid">
            {field('firstName', 'First Name*')}
            {field('lastName', 'Last Name*')}
            {field('email', 'Email Address*', 'email')}
            {field('phone', 'Phone Number*', 'tel')}
            {field('country', 'Country*')}
            {field('jobTitle', 'Job Title')}
          </div>
          {field('company', 'Company Name')}
          <div className="contact-field">
            <label htmlFor="comments" className="sr-only-label">
              Comments / Questions
            </label>
            <textarea
              id="comments"
              name="comments"
              rows={6}
              className="contact-input"
              placeholder="Comments / Questions"
              value={form.comments}
              onChange={(e) => set('comments', e.target.value)}
            />
          </div>

          <div className="contact-consent">
            <input
              type="checkbox"
              id="consent"
              checked={form.consent}
              onChange={(e) => set('consent', e.target.checked)}
              aria-invalid={!!errors.consent}
            />
            <label htmlFor="consent">
              I have read and agree to the <Link to="/terms-use">Terms of use</Link> and{' '}
              <Link to="/privacy-policy">Privacy Policy</Link>
            </label>
          </div>
          {errors.consent && <p className="contact-error">{errors.consent}</p>}

          <div className="contact-submit-row">
            <button type="submit" className="contact-submit">
              Send Message
            </button>
            <p className="contact-direct">
              Prefer your own email? Write to <a href={`mailto:${EMAIL}`}>{EMAIL}</a>
            </p>
          </div>
        </form>
      </PublicCard>
      )}
    </PublicPage>
  );
};

export default ContactUs;
